import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import type { OAuthProvider } from '../types/auth';
import type { InvitePreview } from '@swiss-magic/shared-types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Crown, Mail } from 'lucide-react';
import { showErrorToast } from '@/lib/toast';
import { ApiError } from '@/lib/api/core';
import { claimInviteAccount, getInvitePreview } from '@/lib/api/invite';
import { currentSurface } from '@/lib/appSurface';
import { currentTenantOrNull } from '@/lib/tenants';
import { validatePassword } from '@/lib/passwordPolicy';
import { PublicFooter } from '@/components/PublicFooter';
import { useOrgProfile } from '@/lib/useOrgProfile';
import { Trans, useTranslation } from 'react-i18next';

function validateEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function RegisterPage() {
  const { t, i18n } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const registerFor = searchParams.get('registerFor');
  const followFor = searchParams.get('followFor');
  const subscribeForOrganizerOf = searchParams.get('subscribeForOrganizerOf');
  const inviteToken = searchParams.get('invite');
  const { profile: orgProfile, loading: orgProfileLoading } = useOrgProfile();

  // Story 063 — white-label tenants disable self-registration. Directors
  // are invited via /director-invite/:token; players (a future story) get
  // their own invite path. Story 036's player-row claim invites are still
  // accepted because they go through the invite= query param flow below.
  const tenant = currentTenantOrNull();
  if (tenant && !tenant.allowsSelfRegistration && !inviteToken) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background p-6 pb-[max(theme(spacing.6),env(safe-area-inset-bottom))]">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="mb-2 flex items-center gap-2">
              {orgProfileLoading ? (
                <span
                  data-testid="brand-loading-placeholder"
                  aria-hidden="true"
                  className="h-6 w-6"
                />
              ) : orgProfile?.logoUrl ? (
                <img
                  data-testid="org-logo"
                  src={orgProfile.logoUrl}
                  alt={orgProfile.name}
                  className="max-h-10 max-w-[5rem] object-contain"
                />
              ) : (
                <Crown
                  data-testid="default-brand-badge"
                  className="h-6 w-6 text-accent-foreground"
                />
              )}
              {!orgProfileLoading && (
                <CardTitle>{orgProfile?.name?.trim() || 'Swiss Magic'}</CardTitle>
              )}
            </div>
            <CardDescription
              data-testid="invitation-only-message"
            >
              {t('register.invitation_only')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/login">
              <Button variant="outline" className="w-full">
                {t('register.sign_in_instead')}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [invitePreview, setInvitePreview] = useState<InvitePreview | null>(null);
  const [invitePreviewError, setInvitePreviewError] = useState<string | null>(null);

  // In invite mode, the email is fixed to what the director sent the invite
  // to — clicking the link proves ownership, so we skip Supabase email
  // verification and just ask for a password.
  useEffect(() => {
    if (!inviteToken) return;
    let cancelled = false;
    (async () => {
      try {
        const preview = await getInvitePreview(inviteToken);
        if (cancelled) return;
        setInvitePreview(preview);
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof ApiError
            ? err.userMessage
            : t('register.invite_default_error');
        setInvitePreviewError(message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [inviteToken]);

  async function handleInviteClaim(e: FormEvent) {
    e.preventDefault();
    if (!inviteToken) return;
    if (!validatePassword(password)) {
      showErrorToast(t('register.errors.weak_password'));
      return;
    }
    if (!invitePreview) return;
    setLoading(true);
    try {
      const { email: accountEmail } = await claimInviteAccount(inviteToken, password);
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: accountEmail,
        password,
      });
      if (signInError) {
        showErrorToast(signInError.message);
        return;
      }
      navigate(`/invite/${encodeURIComponent(inviteToken)}`, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) showErrorToast(err.userMessage);
      else showErrorToast(t('register.errors.account_create_failed'));
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSuccess(false);

    if (!validateEmail(email)) {
      showErrorToast(t('register.errors.invalid_email'));
      return;
    }

    if (!validatePassword(password)) {
      showErrorToast(t('register.errors.weak_password'));
      return;
    }

    setLoading(true);
    try {
      const callback = registerFor
        ? `${window.location.origin}/auth/callback?registerFor=${encodeURIComponent(registerFor)}`
        : followFor
          ? `${window.location.origin}/auth/callback?followFor=${encodeURIComponent(followFor)}`
          : subscribeForOrganizerOf
            ? `${window.location.origin}/auth/callback?subscribeForOrganizerOf=${encodeURIComponent(subscribeForOrganizerOf)}`
            : `${window.location.origin}/auth/callback`;
      // Story 070c — stash the current locale in user_metadata so the
      // Send Email Hook can render the confirmation email in the user's
      // language. The user_locale row only exists after first sign-in,
      // and the hook fires before sign-in completes.
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: callback,
          data: { locale: i18n.language },
        },
      });

      if (signUpError) {
        // Supabase has historically used "already registered" but more
        // recent versions use "User already exists" / different
        // phrasings — match liberally.
        const msg = signUpError.message.toLowerCase();
        const code = (signUpError as { code?: string }).code;
        const isWeakPassword =
          code === 'weak_password' ||
          signUpError.name === 'AuthWeakPasswordError' ||
          msg.includes('known to be weak');
        if (isWeakPassword) {
          showErrorToast(t('register.errors.weak_password_breached'));
        } else if (
          msg.includes('already registered') ||
          msg.includes('already exists') ||
          msg.includes('user already') ||
          code === 'user_already_exists'
        ) {
          showErrorToast(t('register.errors.email_taken'));
        } else {
          console.error('SignUp error', signUpError);
          showErrorToast(t('register.errors.unexpected'));
        }
        return;
      }

      // Supabase returns fake success for duplicate emails to prevent
      // enumeration. The empty-identities array is the historical
      // signal; we log the full response so we can extend this
      // detection if the cloud server response shape drifts.
      console.log('[signUp response]', {
        userId: data.user?.id,
        identitiesLength: data.user?.identities?.length,
        email_confirmed_at: data.user?.email_confirmed_at,
        confirmation_sent_at: data.user?.confirmation_sent_at,
        hasSession: data.session !== null,
      });
      const dupByIdentities =
        data.user?.identities !== undefined &&
        data.user.identities.length === 0;
      if (dupByIdentities) {
        showErrorToast(t('register.errors.email_taken'));
        return;
      }

      setSuccess(true);
    } catch {
      showErrorToast(t('register.errors.unexpected'));
    } finally {
      setLoading(false);
    }
  }

  async function handleOAuth(provider: OAuthProvider) {
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (oauthError) {
      // Story 075 (M4) — translated, generic message; raw provider
      // error text goes to the console for debugging only.
      console.error('OAuth error', oauthError);
      showErrorToast(t('register.errors.oauth_failed'));
    }
  }

  return (
    <div data-testid="register-page" className="flex min-h-dvh flex-col items-center bg-background px-4 py-12 pb-[max(theme(spacing.12),env(safe-area-inset-bottom))]">
      <Card className="my-auto w-full max-w-md">
        <CardHeader className="text-center">
          {/* 076 follow-up — same loading-gate as LoginPage. */}
          {orgProfileLoading ? (
            <div
              data-testid="brand-loading-placeholder"
              aria-hidden="true"
              className="mx-auto mb-2 h-12 w-12"
            />
          ) : orgProfile?.logoUrl ? (
            <img
              data-testid="org-logo"
              src={orgProfile.logoUrl}
              alt={orgProfile.name}
              className="mx-auto mb-2 max-h-20 max-w-[12rem] object-contain"
            />
          ) : (
            <div
              data-testid="default-brand-badge"
              className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-accent/20"
            >
              <Crown className="h-6 w-6 text-accent-foreground" />
            </div>
          )}
          <CardTitle className="text-2xl">{t('register.title')}</CardTitle>
          <CardDescription>
            {currentSurface() === 'player'
              ? t('register.description_player')
              : t('register.description_director')}
          </CardDescription>
        </CardHeader>

        <CardContent className="flex flex-col gap-6">
          {inviteToken ? (
            invitePreviewError ? (
              <p
                data-testid="invite-error-message"
                className="text-center text-sm text-destructive"
              >
                {invitePreviewError}
              </p>
            ) : !invitePreview ? (
              <p className="text-center text-muted-foreground">{t('register.loading_invite')}</p>
            ) : (
              <form
                onSubmit={handleInviteClaim}
                noValidate
                data-testid="invite-claim-form"
                className="flex flex-col gap-4"
              >
                <p className="text-sm text-muted-foreground">
                  <Trans
                    t={t}
                    i18nKey="register.invite_intro"
                    values={{ tournamentName: invitePreview.tournamentName }}
                    components={{ strong: <strong /> }}
                  />
                </p>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="invite-email">{t('register.invite_email_label')}</Label>
                  <Input
                    id="invite-email"
                    type="email"
                    value={invitePreview.emailMasked}
                    readOnly
                    disabled
                    data-testid="invite-email-display"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="invite-password">{t('register.invite_password_label')}</Label>
                  <Input
                    id="invite-password"
                    type="password"
                    autoComplete="new-password"
                    placeholder={t('register.invite_password_placeholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="password-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="invite-claim-button"
                  className="w-full"
                >
                  {loading ? t('register.invite_submitting') : t('register.invite_submit')}
                </Button>
                <p className="text-center text-sm text-muted-foreground">
                  {t('register.invite_have_account')}{' '}
                  <Link
                    to={`/login?invite=${encodeURIComponent(inviteToken)}`}
                    className="text-foreground underline underline-offset-4 hover:text-foreground/80"
                  >
                    {t('register.invite_login_link')}
                  </Link>
                </p>
              </form>
            )
          ) : success ? (
            <div data-testid="success-message" className="flex flex-col items-center gap-4 py-4 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                <Mail className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="flex flex-col gap-2">
                <p className="text-lg font-medium text-foreground">{t('register.check_email_title')}</p>
                <p className="text-sm text-muted-foreground">
                  <Trans
                    t={t}
                    i18nKey="register.check_email_body"
                    values={{ email }}
                    components={{ strong: <strong className="text-foreground font-medium" /> }}
                  />
                </p>
              </div>
              <Button asChild variant="outline" className="mt-2 w-full">
                <Link
                  to={{
                    pathname: '/login',
                    search: registerFor
                      ? `?registerFor=${encodeURIComponent(registerFor)}`
                      : followFor
                        ? `?followFor=${encodeURIComponent(followFor)}`
                        : subscribeForOrganizerOf
                          ? `?subscribeForOrganizerOf=${encodeURIComponent(subscribeForOrganizerOf)}`
                          : '',
                  }}
                >
                  {t('register.check_email_back_to_login')}
                </Link>
              </Button>
            </div>
          ) : (
            <>
              <form onSubmit={handleSubmit} noValidate data-testid="register-form" className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="email">{t('register.email_label')}</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder={t('register.email_placeholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="email-input"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="password">{t('register.password_label')}</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="new-password"
                    placeholder={t('register.password_placeholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="password-input"
                  />
                </div>

                <Button type="submit" disabled={loading} data-testid="register-button" className="w-full">
                  {loading ? t('register.submitting') : t('register.submit')}
                </Button>
              </form>

              <div className="flex items-center gap-3">
                <Separator className="flex-1" />
                <span className="text-xs text-muted-foreground uppercase">{t('register.or_continue_with')}</span>
                <Separator className="flex-1" />
              </div>

              <div className="flex flex-col gap-3">
                <Button
                  variant="outline"
                  onClick={() => handleOAuth('google')}
                  data-testid="google-oauth-button"
                  className="w-full"
                >
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                  </svg>
                  {t('register.google')}
                </Button>

                {import.meta.env.VITE_HIDE_APPLE_OAUTH !== 'true' && (
                  <Button
                    variant="outline"
                    onClick={() => handleOAuth('apple')}
                    data-testid="apple-oauth-button"
                    className="w-full"
                  >
                    <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                    </svg>
                    {t('register.apple')}
                  </Button>
                )}
              </div>

              <p className="text-center text-sm text-muted-foreground">
                {t('register.have_account')}{' '}
                <Link
                  to={{
                    pathname: '/login',
                    search: registerFor
                      ? `?registerFor=${encodeURIComponent(registerFor)}`
                      : followFor
                        ? `?followFor=${encodeURIComponent(followFor)}`
                        : subscribeForOrganizerOf
                          ? `?subscribeForOrganizerOf=${encodeURIComponent(subscribeForOrganizerOf)}`
                          : '',
                  }}
                  className="text-foreground underline underline-offset-4 hover:text-foreground/80"
                >
                  {t('register.login_link')}
                </Link>
              </p>
            </>
          )}
        </CardContent>
      </Card>
      <PublicFooter />
    </div>
  );
}
